import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import RoadHomeShield from '../../assets/shield.png';
import './home.css';

const pageTitle = 'Road Home Program | Events and Community';
const pageDescription =
  'Connect with the Road Home Program and explore upcoming events for veterans, service members, families, and supporters.';
const canonicalUrl = 'https://www.roadhome.io/';

function setMetaTag({ selector, tagName, attributes }) {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement(tagName);
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    tag.setAttribute(name, value);
  });
}

function useHomepageMetadata() {
  useEffect(() => {
    document.title = pageTitle;

    setMetaTag({
      selector: 'meta[name="description"]',
      tagName: 'meta',
      attributes: { name: 'description', content: pageDescription },
    });
    setMetaTag({
      selector: 'link[rel="canonical"]',
      tagName: 'link',
      attributes: { rel: 'canonical', href: canonicalUrl },
    });
  }, []);
}

export default function HomePage() {
  useHomepageMetadata();

  return (
    <div className="road-home-page">
      <a className="road-home-skip-link" href="#main-content">
        Skip to main content
      </a>

      <header className="road-home-header">
        <div className="road-home-shell road-home-header__inner">
          <Link className="road-home-brand" to="/" aria-label="Road Home Program home">
            <img
              className="road-home-brand__logo"
              src={RoadHomeShield}
              alt="Road Home Program shield logo"
            />
            <span className="road-home-brand__name" aria-hidden="true">
              Road Home Program
            </span>
          </Link>

          <nav className="road-home-nav" aria-label="Primary navigation">
            <a href="#upcoming-events">Upcoming events</a>
            <Link to="/signup">Join our mailing list</Link>
            <Link className="road-home-nav__staff" to="/login">
              Staff sign in
            </Link>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <section className="road-home-hero" aria-labelledby="road-home-title">
          <div className="road-home-shell road-home-hero__inner">
            <p className="road-home-eyebrow">The Road Home Program at Rush</p>
            <h1 id="road-home-title">Care, connection, and community start here.</h1>
            <p className="road-home-hero__intro">
              The Road Home Program supports veterans, service members, and their families with
              care, connection, and community. Explore what is coming up and join us at our next
              event.
            </p>
            <a className="road-home-button road-home-button--primary" href="#upcoming-events">
              Explore upcoming events
            </a>
          </div>
        </section>

        <section
          className="road-home-events road-home-shell"
          id="upcoming-events"
          aria-labelledby="upcoming-events-title"
        >
          <div className="road-home-section-heading">
            <p className="road-home-eyebrow">Gather with Road Home</p>
            <h2 id="upcoming-events-title">Upcoming events</h2>
            <p>Free opportunities to connect with veterans, families, and the wider community.</p>
          </div>

          <article className="road-home-event-card">
            <div className="road-home-event-card__date" aria-hidden="true">
              <span>Oct</span>
              <strong>14</strong>
              <span>2026</span>
            </div>
            <div className="road-home-event-card__content">
              <p className="road-home-event-card__label">Clinical Education / Webinars</p>
              <h3>
                <Link to="/events/clinical-approaches-medical-trauma-2026">
                  Clinical Approaches to Addressing Medical Trauma
                </Link>
              </h3>
              <dl className="road-home-event-card__details">
                <div>
                  <dt>Date</dt>
                  <dd>
                    <time dateTime="2026-10-14">October 14, 2026</time>
                  </dd>
                </div>
                <div>
                  <dt>Speaker</dt>
                  <dd>Sacha McBain, PhD</dd>
                </div>
              </dl>
              <Link
                className="road-home-event-card__link"
                to="/events/clinical-approaches-medical-trauma-2026"
              >
                View event details <span aria-hidden="true">→</span>
              </Link>
            </div>
          </article>

          <article className="road-home-event-card">
            <div className="road-home-event-card__date" aria-hidden="true">
              <span>Oct</span>
              <strong>22</strong>
              <span>2026</span>
            </div>
            <div className="road-home-event-card__content">
              <p className="road-home-event-card__label">Community Events</p>
              <h3>
                <Link to="/events/salute-social-hendos-2026">Hendo's Salute &amp; Social</Link>
              </h3>
              <dl className="road-home-event-card__details">
                <div>
                  <dt>Date</dt>
                  <dd>
                    <time dateTime="2026-10-22">October 22, 2026</time>
                  </dd>
                </div>
                <div>
                  <dt>Location</dt>
                  <dd>Hendo's Barrel House, Temecula, CA</dd>
                </div>
              </dl>
              <Link className="road-home-event-card__link" to="/events/salute-social-hendos-2026">
                View event details <span aria-hidden="true">→</span>
              </Link>
            </div>
          </article>
        </section>

        <section className="road-home-mailing" aria-labelledby="mailing-list-title">
          <div className="road-home-shell road-home-mailing__inner">
            <div>
              <p className="road-home-eyebrow road-home-eyebrow--light">Stay connected</p>
              <h2 id="mailing-list-title">Get Road Home news and event updates.</h2>
              <p>
                Join our mailing list for updates about events, outreach opportunities, services,
                education, and other Road Home Program news.
              </p>
            </div>
            <Link className="road-home-button road-home-button--gold" to="/signup">
              Join our mailing list
            </Link>
          </div>
        </section>
      </main>

      <footer className="road-home-footer">
        <div className="road-home-shell road-home-footer__inner">
          <p>© {new Date().getFullYear()} Road Home Program at Rush.</p>
          <nav className="road-home-footer__nav" aria-label="Footer navigation">
            <Link to="/privacy-policy">Privacy policy</Link>
            <Link to="/login">Staff sign in</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
